import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { authStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import MainLayout from '@/components/layouts/MainLayout';
import { Pencil, Check, X, Share2, Copy, Mail, LogOut, CheckCircle2, Upload } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { validatePassword } from '@/utils/validation';
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LeaveOrganizationModal } from '@/components/modals/LeaveOrganizationModal';
import { toast } from '@/hooks/use-toast';
import { InviteMember } from '@/components/organizations/InviteMember';
import { Badge } from "@/components/ui/badge";
import { useTranslation } from 'react-i18next';
import { FileUpload } from '@/components/ui/file-upload';

// Extend the User interface to include hasLocalPassword
interface ExtendedUser {
  hasLocalPassword?: boolean;
  name?: string;
  email?: string;
  emailVerified?: boolean;
  organizations?: Array<{
    id: string;
    name: string;
    role: string;
    slug: string;
    logo?: string;
  }>;
}

// Define a generic error type
interface ApiError {
  message?: string;
  response?: {
    data?: {
      message?: string;
    };
  };
}

const AccountPage = observer(() => {
  const { t } = useTranslation();
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingOrg, setEditingOrg] = useState<string | null>(null); // orgId
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgSlug, setNewOrgSlug] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const user = authStore.user as ExtendedUser; // Cast to extended user
  const hasLocalPassword = user?.hasLocalPassword ?? false;

  // New state for organization invites
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [copied, setCopied] = useState(false);

  // Add this handler
  const [leavingOrg, setLeavingOrg] = useState<string | null>(null);

  // New state for logo upload
  const [uploadingLogo, setUploadingLogo] = useState<string | null>(null); // orgId

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleUpdateName = async () => {
    try {
      await authStore.updateName(newName);
      setEditingName(false);
    } catch (error) {
      // Error handled by store
    }
  };

  const handleUpdateOrgName = async (orgId: string) => {
    try {
      await authStore.updateOrganizationName(orgId, newOrgName, newOrgSlug);
      setEditingOrg(null);
    } catch (error) {
      // Error handled by store
    }
  };

  const startEditingName = () => {
    setNewName(user?.name || '');
    setEditingName(true);
  };

  const startEditingOrg = (orgId: string, currentName: string, currentSlug: string) => {
    setNewOrgName(currentName);
    setNewOrgSlug(currentSlug);
    setEditingOrg(orgId);
  };

  const handleSendVerification = async () => {
    try {
      const result = await authStore.sendVerificationEmail();
      toast({
        title: t('common.success'),
        description: result.message,
        variant: 'default'
      });
      setVerificationSent(true);
      setCountdown(300);
    } catch (error: unknown) {
      const apiError = error as ApiError;
      toast({
        title: t('common.error'),
        description: apiError.message || t('account.failedToSendVerificationEmail'),
        variant: 'destructive'
      });
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await authStore.verifyEmail(verificationCode);
      toast({
        title: t('common.success'),
        description: result.message,
        variant: 'default'
      });
      setVerificationSent(false);
      setVerificationCode('');
    } catch (error: unknown) {
      const apiError = error as ApiError;
      toast({
        title: t('common.error'),
        description: apiError.message || t('account.failedToVerifyEmail'),
        variant: 'destructive'
      });
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewPassword(value);
    const { score, errors } = validatePassword(value);
    setPasswordStrength(score);
    setPasswordErrors(errors);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      authStore.setError(t('account.passwordsDoNotMatch'));
      return;
    }

    if (passwordErrors.length > 0) {
      authStore.setError(t('account.passwordDoesNotMeetRequirements'));
      return;
    }
    
    try {
      if (hasLocalPassword && !currentPassword) {
        authStore.setError(t('account.currentPasswordRequired'));
        return;
      }

      if (hasLocalPassword) {
        await authStore.updatePassword(currentPassword, newPassword);
        // Clear form on success
        setIsChangingPassword(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordErrors([]);
        authStore.setError(null); // Clear any previous errors
      } else {
        await authStore.setInitialPassword(newPassword);
        setIsChangingPassword(false);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordErrors([]);
        authStore.setError(null); // Clear any previous errors
      }
    } catch (error: unknown) {
      // Handle the error message from the backend
      const apiError = error as ApiError;
      const errorMessage = apiError.response?.data?.message || t('account.failedToUpdatePassword');
      authStore.setError(errorMessage);
    }
  };

  // Handler for email invites
  const handleEmailInvite = async (orgId: string) => {
    try {
      await authStore.sendOrganizationInvite(orgId, inviteEmail);
      toast({
        title: t('account.invitationSent'),
        description: t('account.invitationEmailSentSuccessfully'),
        duration: 300,
      });
    } catch (error) {
      console.error('Failed to send email invite:', error);
      toast({
        title: t('account.emailDeliveryFailed'),
        description: t('account.couldNotSendInvitationEmail'),
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  // Handler for generating invite link
  const handleGenerateInviteLink = async (orgId: string) => {
    try {
      const response = await authStore.generateOrganizationInviteLink(orgId, inviteEmail);
      if (response.inviteUrl) {
        setInviteUrl(response.inviteUrl);
        
        if (!response.emailSent) {
          toast({
            title: t('account.emailDeliveryFailed'),
            description: t('account.inviteLinkGeneratedButEmailFailed'),
            variant: "destructive",
            duration: 2000,
          });
        }
      }
    } catch (error) {
      console.error('Failed to generate invite link:', error);
      toast({
        title: t('common.error'),
        description: t('account.failedToGenerateInviteLink'),
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  // Copy to clipboard handler
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Add this handler
  const handleLeaveOrganization = async (orgId: string) => {
    try {
      await authStore.leaveOrganization(orgId);
      setLeavingOrg(null);
    } catch (error) {
      // Error handled by store
    }
  };

  // New handler for logo upload
  const handleLogoUpload = async (orgId: string, files: File[]) => {
    if (files.length === 0) return;
    
    const file = files[0];
    // Check if file is a PNG
    if (file.type !== 'image/png') {
      toast({
        title: t('common.error'),
        description: t('account.onlyPngSupported'),
        variant: 'destructive'
      });
      return;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: t('common.error'),
        description: t('account.logoTooLarge'),
        variant: 'destructive'
      });
      return;
    }
    
    try {
      setUploadingLogo(orgId);
      await authStore.updateOrganizationLogo(orgId, file);
      toast({
        title: t('common.success'),
        description: t('account.logoUpdated'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('account.failedToUpdateLogo'),
        variant: 'destructive'
      });
    } finally {
      setUploadingLogo(null);
    }
  };

  // Handler for logo deletion
  const handleLogoDelete = async (orgId: string) => {
    try {
      setUploadingLogo(orgId);
      await authStore.deleteOrganizationLogo(orgId);
      toast({
        title: t('common.success'),
        description: t('account.logoDeleted'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('account.failedToDeleteLogo'),
        variant: 'destructive'
      });
    } finally {
      setUploadingLogo(null);
    }
  };

  return (
    <MainLayout>
      <Card className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-6">{t('account.details')}</h1>
        
        <div className="space-y-6">
          {/* Name Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="name">{t('account.name')}</label>
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input
                  id="name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('account.name')}
                />
                <Button size="icon" onClick={handleUpdateName}>
                  <Check className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="outline" onClick={() => setEditingName(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="text-base">{user?.name}</p>
                <Button variant="ghost" size="icon" onClick={startEditingName}>
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Email Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium">{t('account.email')}</h3>
              {user?.emailVerified ? (
                <Badge variant="success" className="flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {t('account.verified')}
                </Badge>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            {!user?.emailVerified && (
              <div className="mt-2">
                <Alert variant="warning" className="mb-2">
                  {t('account.notVerified')}
                </Alert>
                {!verificationSent ? (
                  <Button onClick={handleSendVerification}>
                    {t('account.verifyEmail')}
                  </Button>
                ) : (
                  <form onSubmit={handleVerifyCode} className="space-y-4">
                    <Input
                      type="text"
                      placeholder={t('account.enterVerificationCode')}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      pattern="[0-9]{6}"
                      maxLength={6}
                    />
                    <div className="flex items-center gap-4">
                      <Button type="submit">
                        {t('account.verifyCode')}
                      </Button>
                      {countdown > 0 && (
                        <span className="text-sm text-gray-500">
                          {t('account.codeExpires', { time: formatTime(countdown) })}
                        </span>
                      )}
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Password Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium" htmlFor="password">{t('account.password')}</label>
              {!isChangingPassword && (
                <Button variant="outline" onClick={() => setIsChangingPassword(true)}>
                  {hasLocalPassword ? t('account.changePassword') : t('account.setPassword')}
                </Button>
              )}
            </div>
            
            {isChangingPassword && (
              <form onSubmit={handleChangePassword} className="space-y-4">
                {hasLocalPassword && (
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">{t('account.currentPassword')}</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder={t('account.enterCurrentPassword')}
                      required
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('account.newPassword')}</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={handlePasswordChange}
                    placeholder={t('account.enterNewPassword')}
                    required
                  />
                  <PasswordStrengthIndicator score={passwordStrength} />
                  <PasswordRequirements errors={passwordErrors} />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('account.confirmNewPassword')}</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('account.confirmPassword')}
                    required
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Button 
                    type="submit"
                    disabled={
                      passwordErrors.length > 0 || 
                      !newPassword || 
                      !confirmPassword || 
                      (hasLocalPassword && !currentPassword)
                    }
                  >
                    {hasLocalPassword ? t('account.updatePassword') : t('account.setPassword')}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setIsChangingPassword(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordErrors([]);
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            )}
          </div>
          
          {/* Organizations Section with Logo Upload */}
          {user?.organizations && user.organizations.length > 0 && (
            <div className="space-y-2">
              <h2 className="text-lg font-semibold">{t('account.organizations')}</h2>
              <div className="space-y-4">
                {user.organizations.map((org) => (
                  <div key={org.id} className="border rounded-lg p-4">
                    {editingOrg === org.id ? (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor={`orgName-${org.id}`}>{t('account.organizationName')}</Label>
                          <Input
                            id={`orgName-${org.id}`}
                            value={newOrgName}
                            onChange={(e) => setNewOrgName(e.target.value)}
                            placeholder={t('account.organizationName')}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor={`orgSlug-${org.id}`}>{t('account.slug')}</Label>
                          <Input
                            id={`orgSlug-${org.id}`}
                            value={newOrgSlug}
                            onChange={(e) => setNewOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-'))}
                            placeholder="organization-slug"
                            pattern="[a-z0-9\-]+"
                          />
                          <p className="text-sm text-gray-500">
                            {t('account.onlyLowercaseAllowed')}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button onClick={() => handleUpdateOrgName(org.id)}>
                            {t('account.saveChanges')}
                          </Button>
                          <Button variant="outline" onClick={() => setEditingOrg(null)}>
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="font-medium">{org.name}</p>
                            <p className="text-sm text-gray-500">{t('account.role')}: {org.role}</p>
                            <p className="text-sm text-gray-500">{t('account.slug')}: {org.slug}</p>
                          </div>
                          <div className="flex gap-2">
                            {org.role === 'OWNER' ? (
                              <>
                                <Button
                                  variant="outline"
                                  onClick={() => startEditingOrg(org.id, org.name, org.slug)}
                                >
                                  <Pencil className="w-4 h-4 mr-2" />
                                  {t('account.edit')}
                                </Button>
                                <InviteMember
                                  organizationId={org.id}
                                  organizationName={org.name}
                                  trigger={
                                    <Button variant="outline">
                                      <Share2 className="w-4 h-4 mr-2" />
                                      {t('account.inviteMembers')}
                                    </Button>
                                  }
                                />
                              </>
                            ) : (
                              <Button
                                variant="destructive"
                                onClick={() => setLeavingOrg(org.id)}
                              >
                                <LogOut className="w-4 h-4 mr-2" />
                                {t('account.leave')}
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Logo Upload Section - Only for OWNER */}
                        {org.role === 'OWNER' && (
                          <div className="mt-4 border-t pt-4">
                            <Label className="mb-2 block">{t('account.organizationLogo')}</Label>
                            <div className="flex flex-col sm:flex-row gap-4 items-start">
                              <div className="w-full sm:w-1/2">
                                <FileUpload
                                  onDrop={(files) => handleLogoUpload(org.id, files)}
                                  value={org.logo || null}
                                  maxSize={2 * 1024 * 1024} // 2MB
                                  accept={{ 'image/png': ['.png'] }}
                                  onClear={() => handleLogoDelete(org.id)}
                                />
                              </div>
                              <div className="w-full sm:w-1/2">
                                <div className="bg-gray-50 p-4 rounded-md">
                                  <p className="text-sm font-medium mb-2">{t('account.logoRequirements')}</p>
                                  <ul className="text-xs text-gray-600 space-y-1 list-disc pl-4">
                                    <li>{t('account.pngFormat')}</li>
                                    <li>{t('account.maxSize2MB')}</li>
                                    <li>{t('account.recommendedSize')}</li>
                                    <li>{t('account.transparentBackground')}</li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                            {uploadingLogo === org.id && (
                              <div className="mt-2">
                                <p className="text-sm text-primary animate-pulse flex items-center">
                                  <Upload className="w-4 h-4 mr-1 animate-bounce" />
                                  {t('account.uploadingLogo')}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add the leave organization modal */}
          {leavingOrg && (
            <LeaveOrganizationModal
              open={!!leavingOrg}
              onOpenChange={(open) => !open && setLeavingOrg(null)}
              organizationName={
                user?.organizations?.find((org) => org.id === leavingOrg)?.name || ''
              }
              onConfirm={() => handleLeaveOrganization(leavingOrg)}
            />
          )}

          {authStore.error && (
            <Alert variant="destructive">
              {authStore.error}
            </Alert>
          )}
        </div>
      </Card>
    </MainLayout>
  );
});

export default AccountPage; 